import React from "react";
import { Link } from "react-router-dom";
import "./ShakeChillies.css";

const rapidApi = {
  company:
    "https://rapidapi.com/shake-chillies-shake-chillies-default/api/crunchbase4?utm_source=leadzlab&utm_medium=canonical_guide&utm_campaign=company_profile_enrichment",
  funding:
    "https://rapidapi.com/shake-chillies-shake-chillies-default/api/funding-tracker?utm_source=leadzlab&utm_medium=canonical_guide&utm_campaign=funding_tracker",
  team:
    "https://rapidapi.com/team/shake-chillies-shake-chillies-default?utm_source=leadzlab&utm_medium=shakechillies_home&utm_campaign=api_catalogue",
};

const siteUrl = "https://leadzlab.co";

function upsertMeta(selector, createAttrs, valueAttr, value) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement(createAttrs.rel ? "link" : "meta");
    Object.entries(createAttrs).forEach(([key, attrValue]) => {
      tag.setAttribute(key, attrValue);
    });
    document.head.appendChild(tag);
  }
  tag.setAttribute(valueAttr, value);
}

function PageMeta({
  title,
  description,
  path,
  image = "https://storage.googleapis.com/leadzlab/MainLogo.png",
  type = "website",
  jsonLd,
}) {
  React.useEffect(() => {
    const url = `${siteUrl}${path}`;
    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description" }, "content", description);
    upsertMeta('link[rel="canonical"]', { rel: "canonical" }, "href", url);
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, "content", title);
    upsertMeta('meta[property="og:description"]', { property: "og:description" }, "content", description);
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, "content", url);
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, "content", type);
    upsertMeta('meta[property="og:image"]', { property: "og:image" }, "content", image);
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "content", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, "content", title);
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, "content", description);
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, "content", image);

    const scriptId = "shakechillies-jsonld";
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [description, image, jsonLd, path, title, type]);

  return null;
}

function articleJsonLd({ headline, description, image, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline,
    description,
    image,
    author: {
      "@type": "Organization",
      name: "ShakeChillies",
    },
    publisher: {
      "@type": "Organization",
      name: "LeadZLab",
      logo: {
        "@type": "ImageObject",
        url: "https://storage.googleapis.com/leadzlab/MainLogo.png",
      },
    },
    datePublished: "2026-07-12",
    dateModified: "2026-07-12",
    mainEntityOfPage: `${siteUrl}${path}`,
  };
}

const guides = [
  {
    title: "Your company data is probably messier than you think",
    description:
      "For teams dealing with duplicate accounts, missing websites, weak industries, and company records that are hard to trust.",
    href: "/shakechillies/guides/messy-company-data",
    image: "/shakechillies/assets/messy-company-data-cover.svg",
    tag: "CRM cleanup",
    readTime: "5 min read",
  },
  {
    title: "The boring data problem behind KYB onboarding",
    description:
      "Why compliance and onboarding teams often need basic company context before deeper verification and review.",
    href: "/shakechillies/guides/kyb-onboarding-company-context",
    image: "/shakechillies/assets/kyb-onboarding-cover.png",
    tag: "KYB",
    readTime: "5 min read",
  },
  {
    title: "Stop asking interns to Google 500 companies",
    description:
      "A workflow for turning company-name spreadsheets into usable research rows without doing every lookup by hand.",
    href: "/shakechillies/guides/manual-company-research-spreadsheets",
    image: "/shakechillies/assets/manual-research-cover.svg",
    tag: "Research ops",
    readTime: "5 min read",
  },
  {
    title: "What should a company research row actually contain?",
    description:
      "A practical row shape for analysts, startup researchers, dashboards, market maps, and internal tools.",
    href: "/shakechillies/guides/company-research-row-schema",
    image: "/shakechillies/assets/research-row-cover.svg",
    tag: "Research schema",
    readTime: "5 min read",
  },
  {
    title: "Why your CRM has company names but no company context",
    description:
      "How to add useful account context for segmentation, routing, reporting, and sales research without blindly overwriting CRM data.",
    href: "/shakechillies/guides/crm-company-context",
    image: "/shakechillies/assets/crm-context-cover.svg",
    tag: "CRM enrichment",
    readTime: "5 min read",
  },
  {
    title: "Enrich company profiles from public business data",
    description:
      "A practical walkthrough for turning a company name into structured fields like website, location, industries, size, and optional funding data.",
    href: "/shakechillies/guides/company-profile-enrichment",
    image: "/shakechillies/assets/company-profile-enrichment-cover.png",
    tag: "Company enrichment",
    readTime: "5 min read",
  },
  {
    title: "Track newly funded startups in 10 minutes",
    description:
      "Build a simple research or prospecting workflow from recent funding signals, stages, countries, investors, websites, and LinkedIn URLs.",
    href: "/shakechillies/guides/startup-funding-tracker",
    image: "/shakechillies/assets/funding-tracker-cover.png",
    tag: "Funding signals",
    readTime: "5 min read",
  },
];

function Header() {
  return (
    <header className="sc-nav">
      <Link className="sc-brand" to="/shakechillies">
        <span className="sc-brand-mark">
          <img
            src="/shakechillies/assets/shakechillies-avatar.png"
            alt=""
          />
        </span>
        <strong>ShakeChillies</strong>
      </Link>
      <nav>
        <Link to="/shakechillies">Home</Link>
        <a href="/shakechillies/guides">Guides</a>
        <Link to="/shakechillies/apis">APIs</Link>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="sc-footer">
      <p>A ShakeChillies project, hosted under LeadZLab.</p>
      <a
        href={rapidApi.team}
        target="_blank"
        rel="noopener noreferrer"
      >
        View ShakeChillies on RapidAPI
      </a>
    </footer>
  );
}

export function ShakeChilliesHome() {
  return (
    <main className="sc-page">
      <PageMeta
        title="ShakeChillies API Guides | LeadZLab"
        description="Developer-friendly ShakeChillies guides for company research, lead enrichment, funding-signal monitoring, and related automation workflows."
        path="/shakechillies"
      />
      <Header />
      <div
        className="sc-brand-ribbon"
        aria-hidden="true"
        style={{
          backgroundImage:
            'url("/shakechillies/assets/shakechillies-pattern.webp")',
        }}
      />
      <section className="sc-hero">
        <div className="sc-hero-copy">
          <p className="sc-eyebrow">ShakeChillies · Company data ops</p>
          <h1>Make messy company research useful.</h1>
          <p>
            Practical guides and focused APIs for the teams who have to turn
            names, spreadsheets, and half-complete records into decisions.
          </p>
          <div className="sc-hero-actions">
            <a href="/shakechillies/guides" className="sc-button primary">Read the guides</a>
            <Link to="/shakechillies/apis" className="sc-button secondary">See the APIs</Link>
          </div>
          <div className="sc-proof-row" aria-label="Guide collection details">
            <span><strong>7</strong> practical guides</span>
            <span><strong>4</strong> workflow lanes</span>
            <span><strong>1</strong> focused data series</span>
          </div>
        </div>
        <div className="sc-hero-card">
          <p className="sc-card-kicker">Start with the problem</p>
          <h2>Your team has the company name. The useful context is missing.</h2>
          <div className="sc-hero-flow"><span>Company name</span><b>→</b><span>Profile context</span><b>→</b><span>Next decision</span></div>
          <p>Use the series to move from messy records to a repeatable research workflow.</p>
          <a href="/shakechillies/guides/messy-company-data">Start with messy CRM data →</a>
        </div>
      </section>
      <section className="sc-home-section" aria-labelledby="home-featured-title">
        <div className="sc-home-section-head">
          <div><p className="sc-eyebrow">Company Data Ops Series</p><h2 id="home-featured-title">Read by workflow, not by endpoint.</h2></div>
          <Link className="sc-text-link" to="/shakechillies/guides">View all guides →</Link>
        </div>
        <div className="sc-home-grid">
          {guides.slice(0, 3).map((guide, index) => (
            <a className={`sc-home-guide ${index === 0 ? "featured" : ""}`} href={guide.href} key={guide.href}>
              <img src={guide.image} alt="" />
              <div className="sc-home-guide-body">
                <div className="sc-guide-meta"><span>{guide.tag}</span><span>{guide.readTime}</span></div>
                <h3>{guide.title}</h3><p>{guide.description}</p><strong>Read guide →</strong>
              </div>
            </a>
          ))}
        </div>
      </section>
      <section className="sc-home-section sc-audience-section" aria-labelledby="home-audience-title">
        <div className="sc-home-section-head"><div><p className="sc-eyebrow">Find your starting point</p><h2 id="home-audience-title">Built around real work.</h2></div></div>
        <div className="sc-audience-grid">
          <a href="/shakechillies/guides/messy-company-data"><span>01</span><h3>CRM & sales ops</h3><p>Clean up account context before routing, segmentation, and outreach.</p></a>
          <a href="/shakechillies/guides/kyb-onboarding-company-context"><span>02</span><h3>KYB & onboarding</h3><p>Give reviewers useful company context without pretending it is final verification.</p></a>
          <a href="/shakechillies/guides/manual-company-research-spreadsheets"><span>03</span><h3>Research & spreadsheets</h3><p>Replace repetitive lookups with a row shape your team can actually use.</p></a>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function CatalogueHeader({ eyebrow, title, description }) {
  return (
    <header className="sc-catalogue-head">
      <p className="sc-eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{description}</p>
    </header>
  );
}

export function GuidesCatalogue() {
  return (
    <main className="sc-page">
      <PageMeta
        title="ShakeChillies API Guides | LeadZLab"
        description="Practical ShakeChillies API guides for company enrichment, startup funding tracking, and developer data workflows on RapidAPI."
        path="/shakechillies/guides"
      />
      <Header />
      <section className="sc-catalogue">
        <CatalogueHeader
          eyebrow="Guides"
          title="Practical API workflows for company research and data teams."
          description="Clear tutorials for turning ShakeChillies endpoints into useful enrichment, research, and monitoring workflows."
        />
        <div className="sc-guide-grid">
          {guides.map((guide) => (
            <a className="sc-guide-card" href={guide.href} key={guide.href}>
              <img src={guide.image} alt="" />
              <div className="sc-guide-meta">
                <span>{guide.tag}</span>
                <span>{guide.readTime}</span>
              </div>
              <h2>{guide.title}</h2>
              <p>{guide.description}</p>
              <strong>Read guide →</strong>
            </a>
          ))}
        </div>
      </section>
      <Footer />
    </main>
  );
}

export function APIsCatalogue() {
  return (
    <main className="sc-page">
      <PageMeta
        title="ShakeChillies API Catalogue | LeadZLab"
        description="Browse focused ShakeChillies APIs for company enrichment, startup funding tracking, and useful B2B data workflows on RapidAPI."
        path="/shakechillies/apis"
      />
      <Header />
      <section className="sc-catalogue">
        <CatalogueHeader
          eyebrow="API catalogue"
          title="Focused APIs for useful B2B data."
          description="Each API is available through RapidAPI, with an interactive playground for testing requests before you integrate."
        />
        <div className="sc-api-catalogue">
          <article className="sc-api-card">
            <div>
              <p className="sc-api-number">01</p>
              <h2>Company profile API</h2>
              <p>
                Enrich a company name with structured fields including its
                website, location, industries, size, description, and optional
                funding data.
              </p>
            </div>
            <div className="sc-card-actions">
              <a href="/shakechillies/guides/company-profile-enrichment">
                Read the guide
              </a>
              <a
                className="sc-button primary"
                href={rapidApi.company}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on RapidAPI ↗
              </a>
            </div>
          </article>
          <article className="sc-api-card">
            <div>
              <p className="sc-api-number">02</p>
              <h2>Funding Tracker API</h2>
              <p>
                Find recent startup funding signals and filter structured
                results by stage, country, industry, time period, and other
                useful fields.
              </p>
            </div>
            <div className="sc-card-actions">
              <a href="/shakechillies/guides/startup-funding-tracker">
                Read the guide
              </a>
              <a
                className="sc-button primary"
                href={rapidApi.funding}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on RapidAPI ↗
              </a>
            </div>
          </article>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function GuideShell({
  children,
  title,
  metaTitle,
  metaDescription,
  subtitle,
  cover,
  coverAlt,
  apiHref,
  path,
}) {
  const absoluteCover = `${siteUrl}${cover}`;
  return (
    <main className="sc-page">
      <PageMeta
        title={metaTitle}
        description={metaDescription}
        path={path}
        image={absoluteCover}
        type="article"
        jsonLd={articleJsonLd({
          headline: title,
          description: metaDescription,
          image: absoluteCover,
          path,
        })}
      />
      <Header />
      <article className="sc-article">
        <header className="sc-article-header">
          <Link className="sc-back" to="/shakechillies">
            ← All guides
          </Link>
          <p className="sc-eyebrow">ShakeChillies Guide</p>
          <h1>{title}</h1>
          <p className="sc-subtitle">{subtitle}</p>
          <div className="sc-article-meta" aria-label="Article details">
            <span>Practical tutorial</span>
            <span>About 5 minutes</span>
          </div>
        </header>
        <img className="sc-cover" src={cover} alt={coverAlt} />
        <div className="sc-article-body">{children}</div>
        <aside className="sc-resource-box">
          <h2>Try the API used in this guide</h2>
          <p>
            This guide uses a hosted ShakeChillies endpoint available through
            RapidAPI. Review the live playground for the exact request format
            before using it in production.
          </p>
          <a
            className="sc-button primary"
            href={apiHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open RapidAPI listing
          </a>
        </aside>
      </article>
      <Footer />
    </main>
  );
}

export function CompanyProfileGuide() {
  return (
    <GuideShell
      title="How to Enrich Company Profiles from Public Business Data in 10 Minutes"
      metaTitle="Company Profile Enrichment API Guide | ShakeChillies"
      metaDescription="Learn how to enrich company profiles with structured business data using the ShakeChillies API on RapidAPI. Includes example requests and production tips."
      subtitle="Turn a company name into structured profile fields your lead research, CRM enrichment, or market-intelligence workflow can actually use."
      cover="/shakechillies/assets/company-profile-enrichment-cover.png"
      coverAlt="Company profile enrichment workflow showing a company name turning into structured profile fields"
      apiHref={rapidApi.company}
      path="/shakechillies/guides/company-profile-enrichment"
    >
      <p>
        If you are building a lead enrichment tool, investor research workflow,
        CRM enrichment step, or internal company-research dashboard, you usually
        need the same company facts again and again: what the company does,
        where it is based, when it was founded, what industries it belongs to,
        its website, team size, and sometimes funding information.
      </p>
      <p>
        The ShakeChillies company intelligence API gives you a simple way to
        fetch structured company profile data from RapidAPI.
      </p>

      <h2>What you can build with this API</h2>
      <p>
        Company enrichment is useful when you already have a company name and
        need a cleaner profile for sales, research, CRM enrichment, or internal
        tools.
      </p>
      <ul>
        <li>Enrich company names inside a CRM or lead list.</li>
        <li>Build a startup or company research workflow.</li>
        <li>Add company context to sales intelligence tools.</li>
        <li>Normalize company details for internal dashboards.</li>
        <li>Pull company profile fields without building your own data collection layer.</li>
      </ul>

      <h2>API used in this tutorial</h2>
      <p>
        RapidAPI listing:{" "}
        <a href={rapidApi.company} target="_blank" rel="noopener noreferrer">
          Company profile enrichment API by ShakeChillies
        </a>
      </p>
      <p>
        Main endpoint: <code>POST /company</code>
      </p>
      <p>
        The endpoint returns fields such as <code>about</code>,{" "}
        <code>founded_year</code>, <code>funding</code>,{" "}
        <code>industries</code>, <code>location</code>,{" "}
        <code>long_description</code>, <code>size</code>, and{" "}
        <code>website</code>.
      </p>

      <h2>Before you start</h2>
      <ol>
        <li>Create or open your RapidAPI account.</li>
        <li>Subscribe to the company intelligence listing on RapidAPI.</li>
        <li>Copy your RapidAPI key from the endpoint playground.</li>
      </ol>

      <h2>Example response shape</h2>
      <pre>{`{
  "company": {
    "about": "Apple is a technology company that designs, manufactures, and markets consumer electronics, personal computers, and software.",
    "founded_year": "1976-04-01",
    "funding": {
      "currency": "USD",
      "value": 1170230000,
      "value_usd": 1170230000
    },
    "industries": ["Apps", "Information Technology", "Mobile Devices"],
    "location": "Cupertino, California, United States",
    "size": "10001+",
    "website": "https://www.apple.com"
  }
}`}</pre>
      <p>
        Treat funding data as optional. Some company profiles will not include a
        funding object, so production code should never assume it exists.
      </p>

      <h2>Example request in JavaScript</h2>
      <pre>{`const url = "https://crunchbase4.p.rapidapi.com/company";

const options = {
  method: "POST",
  headers: {
    "content-type": "application/json",
    "X-RapidAPI-Key": "YOUR_RAPIDAPI_KEY",
    "X-RapidAPI-Host": "crunchbase4.p.rapidapi.com"
  },
  body: JSON.stringify({ company: "Apple" })
};

const response = await fetch(url, options);
const data = await response.json();

console.log(data.company);`}</pre>
      <p>
        If your RapidAPI playground shows a slightly different request body, use
        the values from the playground.
      </p>

      <h2>Handling optional funding data</h2>
      <pre>{`const company = data.company;

const row = {
  name: "Apple",
  website: company.website ?? null,
  location: company.location ?? null,
  industries: company.industries ?? [],
  fundingUsd: company.funding?.value_usd ?? null
};`}</pre>

      <h2>Common workflows</h2>
      <h3>Lead enrichment</h3>
      <p>
        Start with a company name from a signup form, CRM row, or lead list. Use
        the API to add website, location, industry, and company-size data.
      </p>
      <h3>Investor research</h3>
      <p>
        Look up companies in a market category and store structured profile
        fields for internal research.
      </p>
      <h3>Sales intelligence</h3>
      <p>
        Add context to accounts before outreach so sales or growth workflows can
        prioritize better-fit companies.
      </p>
      <h3>Data normalization</h3>
      <p>
        Use the API response as a cleaner profile object when different systems
        store company information inconsistently.
      </p>

      <h2>Production notes</h2>
      <ul>
        <li>Cache repeated lookups so you do not enrich the same company again.</li>
        <li>Store the original search input next to the returned profile.</li>
        <li>Log failed lookups for manual review or retry.</li>
        <li>Start with a small batch before running a large enrichment job.</li>
      </ul>
      <p>
        Disclosure: this guide is maintained by ShakeChillies. Product language
        should not be read as an official Crunchbase affiliation unless
        explicitly stated on the RapidAPI listing.
      </p>
      <p>
        Related:{" "}
        <a href="/shakechillies/guides/startup-funding-tracker">
          Track newly funded startups with the Funding Tracker API
        </a>
        .
      </p>
    </GuideShell>
  );
}

export function FundingTrackerGuide() {
  return (
    <GuideShell
      title="How to track newly funded startups in 10 minutes"
      metaTitle="Startup Funding Tracker API Guide | ShakeChillies"
      metaDescription="Learn how to track newly funded startups with the ShakeChillies Funding Tracker API on RapidAPI. Includes filters, examples, and workflow ideas."
      subtitle="Use funding signals to build a lightweight prospecting, research, or market-monitoring workflow."
      cover="/shakechillies/assets/funding-tracker-cover.png"
      coverAlt="Startup funding tracker workflow showing recently funded companies becoming a lead list"
      apiHref={rapidApi.funding}
      path="/shakechillies/guides/startup-funding-tracker"
    >
      <h2>What this workflow is for</h2>
      <p>
        Recently funded startups can be useful signals for sales teams,
        researchers, investors, recruiters, and operators. The goal is not just
        to read funding news; it is to turn funding activity into structured
        data you can filter and act on.
      </p>
      <ul>
        <li>Find startups that recently raised funding.</li>
        <li>Filter by funding stage, country, industry, or time period.</li>
        <li>Build a weekly funding digest or prospecting list.</li>
        <li>Export structured rows into a CRM, spreadsheet, or research tool.</li>
      </ul>

      <h2>Useful endpoints</h2>
      <ul>
        <li>
          <code>GET /funding/search</code> — search funding rounds with filters.
        </li>
        <li>
          <code>GET /funding/enums</code> — fetch allowed values for filters.
        </li>
      </ul>

      <h2>Example workflow</h2>
      <ol>
        <li>Fetch allowed filters from the enums endpoint.</li>
        <li>Search recent rounds for a narrow time window.</li>
        <li>Filter by stage, country, or industry.</li>
        <li>Map results into clean rows.</li>
        <li>Export or push the list into your next tool.</li>
      </ol>

      <pre>{`const rounds = Array.isArray(data.results) ? data.results : [];

const rows = rounds.map((round) => ({
  company: round.company ?? "Unknown company",
  stage: round.stage ?? null,
  amount: round.amount ?? null,
  date: round.date ?? null,
  investors: round.investors ?? [],
  website: round.website ?? null,
  linkedinUrl: round.linkedin_url ?? null
}));`}</pre>

      <h2>Production notes</h2>
      <ul>
        <li>Start with narrow filters before running broad searches.</li>
        <li>Treat amount, investors, website, and LinkedIn URL as optional.</li>
        <li>Store the date when you fetched the data.</li>
        <li>Review results before triggering automated outreach.</li>
      </ul>
      <p>
        Disclosure: this guide is maintained by ShakeChillies. Sample results
        are illustrative; supported fields and filters should be checked against
        the current RapidAPI playground.
      </p>
      <p>
        Related:{" "}
        <a href="/shakechillies/guides/company-profile-enrichment">
          Enrich company profiles with structured business data
        </a>
        .
      </p>
    </GuideShell>
  );
}
